import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { TeamProjectActivityEvent } from '../client';
import { useApi } from '../state/ApiContext';
import AsyncButton from '../components/AsyncButton';
import { eventTextWithTarget, formatTime } from './ActivityFeed';

/**
 * Did the server REFUSE this list, or did it FAIL?
 *
 * `requirePermission` answers 403 when the caller can see the resource but
 * not at the level asked for, and 404 when they hold nothing at all (it
 * hides existence rather than confirming a team they can't read). Both are
 * correct answers to "may I see this", not faults — so neither is an error
 * to put in front of the user. A 500, a timeout, or an axios error with no
 * response at all IS a fault and must stay visible.
 */
function isAccessRefusal(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  return status === 403 || status === 404;
}

interface RecentActivityListProps {
  teamId: string;
  /** Only activity performed by this team member */
  actorUserId?: number;
  /** Only activity performed by this agent */
  actorAgentId?: number;
  pageSize?: number;
  emptyText?: string;
  /** data-testid prefix so multiple lists can coexist in tests */
  testId?: string;
  /** Called when a row's card/project link is followed (e.g. to close a host modal) */
  onNavigate?: () => void;
  /**
   * Called when the server refuses this list (see `isAccessRefusal`), so a
   * host can drop its heading too instead of framing an empty box. The list
   * renders nothing either way.
   */
  onAccessDenied?: () => void;
}

/**
 * Paged project/card activity feed, optionally filtered to one actor.
 * Used by the team home page, the member detail modal, and the agent
 * Activities view. Rows show what happened plus a link to the card or
 * project it happened on.
 */
export const RecentActivityList: React.FC<RecentActivityListProps> = ({
  teamId,
  actorUserId,
  actorAgentId,
  pageSize = 20,
  emptyText = 'No project activity yet.',
  testId = 'recent-activity',
  onNavigate,
  onAccessDenied,
}) => {
  const { projectsApi } = useApi();
  const [events, setEvents] = useState<TeamProjectActivityEvent[]>([]);
  const [nextBeforeId, setNextBeforeId] = useState<number | undefined>();
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [denied, setDenied] = useState(false);
  // Held in a ref so an inline callback from the host doesn't change `load`'s
  // identity and re-trigger the fetch effect on every render.
  const onAccessDeniedRef = useRef(onAccessDenied);
  useEffect(() => {
    onAccessDeniedRef.current = onAccessDenied;
  }, [onAccessDenied]);

  const load = useCallback(
    async (beforeId?: number) => {
      beforeId ? setLoadingMore(true) : setLoading(true);
      try {
        const response = await projectsApi.apiTeamsTeamIdProjectActivityGet(
          Number(teamId),
          beforeId,
          pageSize,
          actorUserId,
          actorAgentId,
        );
        const incoming = response.data.activity || [];
        setEvents((current) => {
          const combined = beforeId ? [...current, ...incoming] : incoming;
          return combined.filter(
            (event, index) =>
              combined.findIndex((candidate) => candidate.id === event.id) ===
              index,
          );
        });
        setNextBeforeId(response.data.nextBeforeId);
        setHasMore(!!response.data.hasMore);
        setError(null);
      } catch (requestError) {
        if (isAccessRefusal(requestError)) {
          // Not an error: this caller simply may not read team activity.
          // Say nothing at all — the team home page hides summary sections
          // it can't fill rather than explaining the gap, the same as
          // Pending Approvals and Conversations.
          setDenied(true);
          setError(null);
          setHasMore(false);
          onAccessDeniedRef.current?.();
          return;
        }
        console.error('Error loading recent activity:', requestError);
        setError(
          beforeId
            ? 'Could not load more activity.'
            : 'Could not load recent activity.',
        );
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [projectsApi, teamId, pageSize, actorUserId, actorAgentId],
  );

  useEffect(() => {
    load();
  }, [load]);

  // A refusal renders nothing — no heading-less empty box, no red banner.
  if (denied) return null;

  return (
    <>
      {loading ? (
        <p className="descriptionText">Loading recent activity…</p>
      ) : events.length === 0 && !error ? (
        <div className="projects-empty">{emptyText}</div>
      ) : (
        <div className="activity-feed team-activity-feed">
          {events.map((event) => {
            const path = event.cardId
              ? `/console/team/${teamId}/projects/${event.projectId}/cards/${event.cardId}`
              : `/console/team/${teamId}/projects/${event.projectId}`;
            return (
              <div
                className="activity-row team-activity-row"
                key={event.id}
                data-testid={`${testId}-${event.id}`}>
                <span className="activity-time">
                  {formatTime(event.createdAt)}
                </span>
                <span className="activity-text">
                  {eventTextWithTarget(teamId, event, {
                    path,
                    label:
                      event.cardTitle ||
                      event.projectName ||
                      event.projectKey ||
                      'Open project',
                    onNavigate,
                  })}
                </span>
              </div>
            );
          })}
        </div>
      )}
      {error && <div className="projects-error">{error}</div>}
      {hasMore && nextBeforeId && (
        <AsyncButton
          className="conversations-load-more"
          loading={loadingMore}
          loadingText="Loading..."
          onClick={() => load(nextBeforeId)}>
          {error ? 'Retry load more' : 'Load more'}
        </AsyncButton>
      )}
    </>
  );
};

export default RecentActivityList;
